import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";
import { SiX, SiTiktok, SiYoutube, SiFacebook } from "react-icons/si";
import TikTokPostModal from "../TikTokPostModal/TikTokPostModal";
import "./scheduleModal.css";

// The platforms services/socialPosting.js can post to. Other connections
// (Twitch, Kick) are stream sources, not upload targets. TikTok isn't here:
// it has its own posting screen (TikTokPostModal), admin-only for now.
const PLATFORM_INFO = {
  twitter: { name: "X", Icon: SiX, color: "#000" },
  youtube: { name: "YouTube", Icon: SiYoutube, color: "#FF0000" },
  facebook: { name: "Facebook", Icon: SiFacebook, color: "#1877F2" },
};

const ACTION_OPTIONS = [
  { id: "schedule", labelKey: "scheduleAction", descKey: "saveAndSchedule" },
  { id: "publish", labelKey: "postAction", descKey: "publishImmediately" },
];

const ScheduleModal = ({ clip, clipJobId, clipIndex, onClose, onScheduled, initialAction = "schedule" }) => {
  const { t } = useTranslation('schedule');
  const navigate = useNavigate();
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [scheduledTime, setScheduledTime] = useState("");
  const [caption, setCaption] = useState(clip?.caption || clip?.hook || "");
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [fetchingPlatforms, setFetchingPlatforms] = useState(true);
  const [selectedAction, setSelectedAction] = useState(initialAction);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [postResult, setPostResult] = useState(null); // { status, results }
  const [tiktokReady, setTiktokReady] = useState(false);
  const [tiktokMode, setTiktokMode] = useState(null); // "direct" | "draft" while TikTok's screen is open
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowActionMenu(false);
      }
    };
    if (showActionMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActionMenu]);

  useEffect(() => {
    fetchConnectedPlatforms();
    // Set default time to 1 hour from now
    const defaultTime = new Date(Date.now() + 60 * 60 * 1000);
    setScheduledTime(formatDateTimeLocal(defaultTime));
  }, []);

  const formatDateTimeLocal = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const fetchConnectedPlatforms = async () => {
    try {
      const res = await axiosInstance.get("/users/me/creator-settings");
      const connections = res.data.socialConnections || {};
      const connected = Object.entries(connections)
        .filter(([platform, data]) => data.connected && PLATFORM_INFO[platform])
        .map(([platform]) => platform);
      setConnectedPlatforms(connected);
      setTiktokReady(!!res.data.isAdmin && !!connections.tiktok?.connected);
      // Select all connected platforms by default
      setSelectedPlatforms(connected);
    } catch (err) {
      console.error("Failed to fetch platforms:", err);
      toast.error("Failed to load connected platforms");
    } finally {
      setFetchingPlatforms(false);
    }
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const selectAll = () => {
    setSelectedPlatforms(connectedPlatforms);
  };

  const handleSchedule = async (e) => {
    if (e) e.preventDefault();

    if (selectedPlatforms.length === 0) {
      return toast.error(t('selectAtLeastOnePlatform'));
    }
    if (!scheduledTime) {
      return toast.error(t('selectATime'));
    }

    const scheduleDate = new Date(scheduledTime);
    if (scheduleDate <= new Date()) {
      return toast.error(t('selectFutureTime'));
    }

    setLoading(true);
    try {
      await axiosInstance.post("/scheduled-posts", {
        clipUrl: clip.editedUrl || clip.url,
        clipTitle: clip.hook || clip.title || "",
        clipDuration: clip.duration,
        clipJobId: clipJobId || null,
        clipIndex: clipIndex ?? 0,
        platforms: selectedPlatforms,
        scheduledTime: scheduleDate.toISOString(),
        caption,
      });
      toast.success(t('postScheduled'));
      onScheduled?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToSchedule'));
    } finally {
      setLoading(false);
    }
  };

  const handlePublishNow = async () => {
    if (selectedPlatforms.length === 0) {
      return toast.error(t('selectAtLeastOnePlatform'));
    }

    setPublishing(true);
    setPostResult(null);
    try {
      const res = await axiosInstance.post("/scheduled-posts/publish-now", {
        clipUrl: clip.editedUrl || clip.url,
        clipTitle: clip.hook || clip.title || "",
        clipDuration: clip.duration,
        clipJobId: clipJobId || null,
        clipIndex: clipIndex ?? 0,
        platforms: selectedPlatforms,
        caption,
      });

      // Show results in modal
      setPostResult({
        status: res.data.status,
        results: res.data.results,
      });

      if (res.data.status === "completed") {
        toast.success(t('postedSuccess'));
      } else if (res.data.status === "partial") {
        toast.success(t('postedToSomePlatforms'));
      } else {
        toast.error(t('failedToPostError'));
      }

      onScheduled?.();
    } catch (err) {
      toast.error(err.response?.data?.error || t('failedToPublish'));
      setPostResult(null);
    } finally {
      setPublishing(false);
    }
  };

  const minDateTime = formatDateTimeLocal(new Date());

  if (tiktokMode) {
    return (
      <TikTokPostModal
        clip={clip}
        clipJobId={clipJobId}
        clipIndex={clipIndex}
        initialMode={tiktokMode}
        onClose={() => setTiktokMode(null)}
      />
    );
  }

  const tiktokRow = tiktokReady && (
    <div className="schedule-tiktok">
      <span className="schedule-platform-icon" style={{ backgroundColor: "#000" }}><SiTiktok /></span>
      <button type="button" onClick={() => setTiktokMode("direct")}>Post to TikTok</button>
      <button type="button" onClick={() => setTiktokMode("draft")}>Send to TikTok drafts</button>
    </div>
  );

  return (
    <div className="schedule-modal-overlay" onClick={onClose}>
      <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="schedule-modal-header">
          <h3>{t('shareClip')}</h3>
          <button className="schedule-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Post Results View */}
        {postResult ? (
          <div className="schedule-result">
            <div className={`schedule-result-header ${postResult.status}`}>
              {postResult.status === "completed" ? (
                <>
                  <div className="schedule-result-icon success">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h4>{t('postedSuccessfully')}</h4>
                </>
              ) : postResult.status === "partial" ? (
                <>
                  <div className="schedule-result-icon partial">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                  <h4>{t('partiallyPosted')}</h4>
                </>
              ) : (
                <>
                  <div className="schedule-result-icon failed">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </div>
                  <h4>{t('failedToPost')}</h4>
                </>
              )}
            </div>

            <div className="schedule-result-platforms">
              {postResult.results.map((result) => {
                const info = PLATFORM_INFO[result.name];
                return (
                  <div key={result.name} className={`schedule-result-platform ${result.status}`}>
                    <div className="schedule-result-platform-info">
                      <span
                        className="schedule-platform-icon"
                        style={{ backgroundColor: info?.color || "#333" }}
                      >
                        {info?.icon || result.name[0].toUpperCase()}
                      </span>
                      <span className="schedule-result-platform-name">
                        {info?.name || result.name}
                      </span>
                    </div>
                    <div className="schedule-result-platform-status">
                      {result.status === "posted" ? (
                        <a
                          href={result.postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="schedule-result-link"
                        >
                          {t('viewPost')}
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </a>
                      ) : (
                        <span className="schedule-result-error">
                          {result.error || "Failed"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="schedule-result-actions">
              <button className="schedule-result-done" onClick={onClose}>
                {t('common:done')}
              </button>
            </div>
          </div>
        ) : fetchingPlatforms ? (
          <div className="schedule-modal-loading">{t('loadingPlatforms')}</div>
        ) : connectedPlatforms.length === 0 && tiktokReady ? (
          <div className="schedule-modal-tiktok-only">{tiktokRow}</div>
        ) : connectedPlatforms.length === 0 ? (
          <div className="schedule-modal-empty">
            <p>{t('noConnectedPlatforms')}</p>
            <p>{t('connectSocialsToSchedule')}</p>
            <button className="schedule-modal-goto" onClick={() => { onClose(); navigate("/me"); }}>
              {t('connectYourAccounts')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSchedule}>
            {tiktokRow}
            {/* Platform Pills */}
            <div className="schedule-platforms">
              <div className="schedule-platforms-header">
                <span>{t('postTo')}</span>
                {connectedPlatforms.length > 1 && (
                  <button type="button" onClick={selectAll}>
                    {selectedPlatforms.length === connectedPlatforms.length ? t('deselectAll') : t('selectAll')}
                  </button>
                )}
              </div>
              <div className="schedule-platforms-pills">
                {connectedPlatforms.map((platform) => {
                  const info = PLATFORM_INFO[platform];
                  const isSelected = selectedPlatforms.includes(platform);
                  return (
                    <button
                      key={platform}
                      type="button"
                      className={`schedule-platform-pill ${isSelected ? "selected" : ""}`}
                      onClick={() => togglePlatform(platform)}
                      title={info.name}
                      aria-label={info.name}
                    >
                      <span
                        className="schedule-platform-icon"
                        style={{ backgroundColor: info.color }}
                      >
                        <info.Icon />
                      </span>
                      {isSelected && (
                        <span className="schedule-platform-check">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Caption */}
            <div className="schedule-caption">
              <label>{t('caption')}</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={500}
                placeholder={t('writeCaptionPlaceholder')}
                rows={4}
              />
              <span className="schedule-caption-count">
                {caption.length}/500
              </span>
            </div>

            {/* Schedule DateTime - only show when scheduling */}
            {selectedAction === "schedule" && (
              <div className="schedule-datetime">
                <input
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={minDateTime}
                  required
                />
              </div>
            )}

            {/* Actions - Split Button */}
            <div className="schedule-actions">
              <button
                type="button"
                className="schedule-cancel"
                onClick={onClose}
                disabled={loading || publishing}
              >
                {t('common:cancel')}
              </button>

              <div className="schedule-split-btn" ref={dropdownRef}>
                {/* Main Action Button */}
                <button
                  type="button"
                  className="schedule-main-btn"
                  onClick={selectedAction === "publish" ? handlePublishNow : handleSchedule}
                  disabled={loading || publishing || selectedPlatforms.length === 0}
                >
                  {(loading || publishing) ? (
                    <>
                      <span className="schedule-btn-spinner" />
                      {selectedAction === "publish" ? t('publishing') : t('scheduling')}
                    </>
                  ) : (
                    t(ACTION_OPTIONS.find(a => a.id === selectedAction)?.labelKey || 'scheduleAction')
                  )}
                </button>

                {/* Dropdown Toggle */}
                <button
                  type="button"
                  className="schedule-dropdown-toggle"
                  onClick={() => setShowActionMenu(!showActionMenu)}
                  disabled={loading || publishing}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {showActionMenu && (
                  <div className="schedule-action-menu">
                    {ACTION_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`schedule-action-option ${selectedAction === option.id ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedAction(option.id);
                          setShowActionMenu(false);
                        }}
                      >
                        <div className="schedule-action-option-content">
                          <span className="schedule-action-option-label">{t(option.labelKey)}</span>
                          <span className="schedule-action-option-desc">{t(option.descKey)}</span>
                        </div>
                        {selectedAction === option.id && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ScheduleModal;
